import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useGetForecastingForFysQuery } from '@/api/forecasting';
import { fyCodesInRange } from './utils/fyCalendar';
import type { DashboardFilters } from './forecasting.types';
import type { ChannelMarginRow } from './margin.types';
import {
  filterRows,
  formatFyLabel,
  getDefaultThreeMonthFilters,
  uniqueFyOptions
} from './utils/forecastingAggregations';
import {
  computeMarginWaterfallTotals,
  groupMarginDrillSummary,
  groupMarginPieByBookedPlusPipe,
  groupMonthlyMargin
} from './utils/marginAggregations';
import {
  canDrillDownLevel,
  canDrillUpLevel,
  DRILL_LEVELS,
  drillSelectionLabel,
  filterRowsByDrillSelection,
  getDrillLevelConfig,
  type DrillSelection
} from './utils/drilldownTable';
import { FilterSidebar } from './components/FilterSidebar';
import { MarginKpiScorecards } from './components/MarginKpiScorecards';
import { MarginWaterfallChart } from './components/MarginWaterfallChart';
import { ChannelMarginsTable } from './components/ChannelMarginsTable';
import { ChannelDonutChart } from './components/ChannelDonutChart';
import { MonthlyMarginChart } from './components/MonthlyMarginChart';
import {
  ActiveLensHint,
  ChartRow,
  HeaderLogo,
  HeaderTitle,
  Layout,
  Main,
  Page,
  PageHeader
} from './Forecasting.styles';
import { LOGO_INK, LOGO_WHITE } from '@/assets/brand';
import { useThemeMode } from '@/styles';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const CampaignMargins: FC = () => {
  const { mode: themeMode } = useThemeMode();
  const defaultFilters = useMemo(() => getDefaultThreeMonthFilters(), []);
  const [filters, setFilters] = useState<DashboardFilters>(() => defaultFilters);
  const [levelIndex, setLevelIndex] = useState(0);
  const [drillSelection, setDrillSelection] = useState<DrillSelection>({});
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);

  /* Load every FY the date range reaches, as the full-year page does: a range
     crossing April fetched only its end month's FY, so the months before it
     showed nothing. */
  const fys = useMemo(
    () => fyCodesInRange(filters.dateFrom, filters.dateTo),
    [filters.dateFrom, filters.dateTo]
  );
  const { data: allRows = [], isFetching, isError } = useGetForecastingForFysQuery(
    { fys },
    { skip: !fys.length }
  );
  const fyOptions = useMemo(() => uniqueFyOptions(allRows), [allRows]);
  const filteredRows = useMemo(() => filterRows(allRows, filters), [allRows, filters]);
  const level = useMemo(() => getDrillLevelConfig(levelIndex), [levelIndex]);

  const chartRows = useMemo(
    () => filterRowsByDrillSelection(filteredRows, drillSelection, levelIndex),
    [filteredRows, drillSelection, levelIndex]
  );

  const selectionLabel = useMemo(
    () => drillSelectionLabel(drillSelection, levelIndex),
    [drillSelection, levelIndex]
  );

  const waterfallTotals = useMemo(() => computeMarginWaterfallTotals(chartRows), [chartRows]);
  const marginDrillData = useMemo(
    () => groupMarginDrillSummary(filteredRows, levelIndex),
    [filteredRows, levelIndex]
  );
  const marginPie = useMemo(
    () => groupMarginPieByBookedPlusPipe(chartRows, levelIndex),
    [chartRows, levelIndex]
  );
  const monthlyMargin = useMemo(
    () => groupMonthlyMargin(chartRows, filters.dateFrom, filters.dateTo),
    [chartRows, filters.dateFrom, filters.dateTo]
  );

  useEffect(() => {
    setDrillSelection({});
    setSelectedRowKey(null);
  }, [filters]);

  useEffect(() => {
    setDrillSelection({});
    setSelectedRowKey(null);
  }, [levelIndex]);

  const handleFilterChange = (patch: Partial<DashboardFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      Object.keys(patch).forEach((k) => {
        if (patch[k as keyof DashboardFilters] === undefined) {
          delete next[k as keyof DashboardFilters];
        }
      });
      return next;
    });
  };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
  };

  const handleRowSelect = (row: ChannelMarginRow) => {
    if (selectedRowKey === row.key) {
      setDrillSelection({});
      setSelectedRowKey(null);
      return;
    }

    const nextSelection: DrillSelection = {};
    for (let i = 0; i <= levelIndex; i++) {
      const drillLevel = DRILL_LEVELS[i];
      if (i < levelIndex) {
        const parent = drillSelection[drillLevel.level];
        if (parent) nextSelection[drillLevel.level] = parent;
      } else {
        nextSelection[drillLevel.level] = row.label;
      }
    }

    setDrillSelection(nextSelection);
    setSelectedRowKey(row.key);
  };

  const clearSelection = () => {
    setDrillSelection({});
    setSelectedRowKey(null);
  };

  const drillUp = () => setLevelIndex((i) => Math.max(0, i - 1));
  const drillDown = () => setLevelIndex((i) => Math.min(DRILL_LEVELS.length - 1, i + 1));

  const monthlyChartTitle = selectionLabel ? `B+P, GP & margin · ${selectionLabel}` : 'B+P, GP & margin';

  return (
    <Page>
      <PageHeader>
        <HeaderLogo src={themeMode === 'dark' ? LOGO_WHITE : LOGO_INK} alt="Skyrise" />
        <HeaderTitle>Campaign Margins</HeaderTitle>
      </PageHeader>

      {isError && (
        <Alert type="error" message="Failed to load forecasting data" style={{ marginBottom: 16 }} showIcon />
      )}

      {!isFetching && !isError && allRows.length === 0 && (
        <Alert
          type="warning"
          message={`No forecasting data returned for ${formatFyLabel(filters.fy)}`}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Spin spinning={isFetching}>
        <Layout>
          <FilterSidebar
            filters={filters}
            allRows={allRows}
            fyOptions={fyOptions}
            defaultDateRange={defaultFilters}
            onChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
          />

          <Main>
            <MarginKpiScorecards
              bookedPlusPipe={waterfallTotals.bookedPlusPipe}
              grossProfit={waterfallTotals.grossProfit}
              marginPct={waterfallTotals.marginPct}
            />

            <ActiveLensHint>
              {selectionLabel ? (
                <>
                  Filter: <strong>{selectionLabel}</strong>
                  {' '}
                  <button
                    type="button"
                    onClick={clearSelection}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: `rgba(${APP_SHELL_THEME.overlayRgb}, 0.55)`,
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: 0,
                      textDecoration: 'underline'
                    }}
                  >
                    Clear
                  </button>
                </>
              ) : (
                <>Click a table row to filter charts</>
              )}
            </ActiveLensHint>

            <MonthlyMarginChart data={monthlyMargin} title={monthlyChartTitle} />

            <ChartRow>
              <ChannelMarginsTable
                data={marginDrillData}
                levelTitle={level.title}
                canDrillUp={canDrillUpLevel(levelIndex)}
                canDrillDown={canDrillDownLevel(levelIndex)}
                onDrillUp={drillUp}
                onDrillDown={drillDown}
                selectedKey={selectedRowKey}
                onRowSelect={handleRowSelect}
              />
              <ChannelDonutChart
                data={marginPie}
                metricLabel="B + P"
                title={`${level.title} mix — B + P`}
              />
            </ChartRow>

            <MarginWaterfallChart totals={waterfallTotals} />
          </Main>
        </Layout>
      </Spin>
    </Page>
  );
};

export default CampaignMargins;
